<script setup lang="ts">
// Asks for the four digit code that opens the maintenance panel. Reached only by a
// long press on the logo
const emit = defineEmits<{ close: [], unlocked: [] }>()

const { unlock, lockedFor } = useMaintenanceAccess()
const code = ref('')
const error = ref(false)
const input = ref<HTMLInputElement | null>(null)

useModalBehaviour(() => emit('close'))

onMounted(() => {
  input.value?.focus()
})

const remaining = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

watch(lockedFor, (seconds) => {
  remaining.value = seconds

  if (seconds > 0 && !timer) {
    timer = setInterval(() => {
      remaining.value = lockedFor.value

      if (remaining.value <= 0 && timer) {
        clearInterval(timer)
        timer = null
      }
    }, 500)
  }
}, { immediate: true })

onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
  }
})

// Any characters, so the code can be something like "12323aff!!". Checked on submit
// rather than as it is typed, since there is no fixed length to trigger on.
const submit = () => {
  if (remaining.value > 0) {
    return
  }

  if (unlock(code.value)) {
    emit('unlocked')

    return
  }

  error.value = true
  code.value = ''
}

// Clears the message as soon as someone starts again, rather than leaving a stale
// "wrong code" under a field they are already retyping.
watch(code, () => {
  error.value = false
})
</script>

<template>
  <!-- Tapping outside the panel closes it, the same as the cancel button. -->
  <div
    class="unlock"
    role="dialog"
    aria-modal="true"
    aria-label="Maintenance code"
    @click.self="emit('close')"
  >
    <div class="unlock__panel">
      <h2 class="unlock__title">Maintenance</h2>
      <p class="unlock__hint">Enter the maintenance code.</p>

      <form @submit.prevent="submit">
        <!-- Deliberately not type="password". This is a shared team code, not anyone's
             credential, and a password field makes the browser offer to save it, which
             is both meaningless and a dialog in the way. The characters are still masked,
             by -webkit-text-security on .unlock__input, which is enough on the iOS fleet
             this runs on and hides no less than type="password" did. -->
        <input
          ref="input"
          v-model="code"
          class="unlock__input"
          type="text"
          name="maintenance-code"
          autocomplete="one-time-code"
          autocapitalize="off"
          spellcheck="false"
          :maxlength="MAINTENANCE_CODE_MAX_LENGTH"
          :disabled="remaining > 0"
          aria-label="Maintenance code"
        >
      </form>

      <p v-if="remaining > 0" class="unlock__error" role="status" aria-live="polite">
        Too many attempts. Try again in {{ remaining }}s.
      </p>
      <p v-else-if="error" class="unlock__error" role="status" aria-live="polite">
        Wrong code.
      </p>

      <button
        type="button"
        class="wad4-button wad4-button-primary wad4-button-full"
        :disabled="remaining > 0 || !code"
        @click="submit"
      >
        Open
      </button>

      <button type="button" class="unlock__cancel" @click="emit('close')">
        Cancel
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.unlock {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $wad4-spacing-6;
  background-color: rgba(0, 0, 0, 0.6);

  &__panel {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-4;
    width: 100%;
    max-width: 260px;
    padding: $wad4-spacing-5;
    text-align: center;
    background-color: $wad4-c-background-bg-primary;
    border-radius: $wad4-border-radius-lg;
  }

  &__title {
    margin: 0;
    @include title-display-mixin('xs');
  }

  &__hint {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
  }

  &__input {
    width: 100%;
    height: 44px;
    padding: 0 $wad4-spacing-3;
    font-family: $wad4-default-font-sans;
    // Masks the characters without the field being type="password", which is what made
    // the browser offer to save a shared team code as a credential. WebKit and Blink
    // only, which covers the whole iOS fleet this app runs on.
    -webkit-text-security: disc;
    @include text-size-mixin('md');
    // Enough to tell the hidden characters apart without pushing a ten character
    // code out of the field.
    letter-spacing: 3px;
    text-align: center;
    color: $wad4-c-text-text-primary;
    background-color: $wad4-c-background-bg-secondary;
    border: 1px solid $wad4-c-border-border-primary;
    border-radius: $wad4-border-radius-md;
    -webkit-user-select: text;
    user-select: text;
  }

  &__error {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-bc-error-600;
  }

  &__cancel {
    padding: $wad4-spacing-3;
    font-family: $wad4-default-font-sans;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
    background: none;
    border: none;
    cursor: pointer;
  }
}
</style>
